// Variants and add-ons of a service in the organizer form: form state, validation and request body.
// The limits mirror the back (BeautyService.variants / addOns); the back stays the final judge.

export const MAX_OPCIONES = 10;
export const MAX_NOMBRE = 60;

let contador = 0;
const clave = (prefijo) => {
  contador += 1;
  return `${prefijo}${contador}`;
};

/** A new row. `_id` stays empty until the back creates it; entries that come from the back keep theirs. */
export const nuevaVariante = ({ duracion = "60", precio = "" } = {}) => ({
  key: clave("var"),
  _id: "",
  nombre: "",
  duracion: String(duracion),
  precio: String(precio),
});

export const nuevoAdicional = () => ({ key: clave("add"), _id: "", nombre: "", duracion: "15", precio: "" });

const deBack = (prefijo, o, vacio) => ({
  key: String(o._id || clave(prefijo)),
  _id: String(o._id || ""),
  nombre: o.name || "",
  duracion: String(o.durationMinutes ?? vacio),
  precio: String(o.price ?? ""),
});

export const variantesDe = (servicio) => (servicio?.variants || []).map((v) => deBack("var", v, "60"));
export const adicionalesDe = (servicio) => (servicio?.addOns || []).map((a) => deBack("add", a, "0"));

const entero = (v) => Number.isInteger(Number(v)) && String(v).trim() !== "";
const multiploDe5 = (v, minimo) => entero(v) && Number(v) >= minimo && Number(v) % 5 === 0;
const precioValido = (v) => String(v ?? "").trim() !== "" && Number(v) >= 0;

/** Cheapest variant: lowest price, then shortest (the back derives the service price and duration from it). */
export const masBarata = (variantes) =>
  variantes
    .filter((v) => precioValido(v.precio) && entero(v.duracion))
    .reduce((mejor, v) => {
      if (!mejor) return v;
      const p = Number(v.precio) - Number(mejor.precio);
      return p < 0 || (p === 0 && Number(v.duracion) < Number(mejor.duracion)) ? v : mejor;
    }, null);

const etiqueta = (item, indice, singular) => item.nombre.trim() || `${singular} ${indice + 1}`;

/** First problem of the variants ("" when fine). `sena` is the service deposit as typed. */
export const faltaVariantes = (variantes, sena) => {
  if (!variantes.length) return "Agregá al menos una variante o desactivá «Tiene variantes»";
  if (variantes.length > MAX_OPCIONES) return `Un servicio admite hasta ${MAX_OPCIONES} variantes`;
  for (let i = 0; i < variantes.length; i += 1) {
    const v = variantes[i];
    const nombre = etiqueta(v, i, "la variante");
    if (!v.nombre.trim()) return `Falta el nombre de la variante ${i + 1}`;
    if (v.nombre.trim().length > MAX_NOMBRE) return `El nombre de ${nombre} supera los ${MAX_NOMBRE} caracteres`;
    if (!multiploDe5(v.duracion, 5)) return `La duración de ${nombre} debe ser múltiplo de 5 minutos`;
    if (!precioValido(v.precio)) return `Falta el precio de ${nombre}`;
  }
  const barata = masBarata(variantes);
  if (sena !== "" && sena != null && barata && Number(sena) > Number(barata.precio)) {
    return "La seña no puede superar la variante más barata";
  }
  return "";
};

/** First problem of the add-ons ("" when fine). They may add 0 minutes (a quick extra) but never a negative price. */
export const faltaAdicionales = (adicionales) => {
  if (adicionales.length > MAX_OPCIONES) return `Un servicio admite hasta ${MAX_OPCIONES} adicionales`;
  for (let i = 0; i < adicionales.length; i += 1) {
    const a = adicionales[i];
    const nombre = etiqueta(a, i, "el adicional");
    if (!a.nombre.trim()) return `Falta el nombre del adicional ${i + 1}`;
    if (a.nombre.trim().length > MAX_NOMBRE) return `El nombre de ${nombre} supera los ${MAX_NOMBRE} caracteres`;
    if (!multiploDe5(a.duracion, 0)) return `Lo que suma ${nombre} debe ser múltiplo de 5 minutos`;
    if (!precioValido(a.precio)) return `Falta lo que suma ${nombre} al precio`;
  }
  return "";
};

/** Full list for the back; entries that already existed keep their `_id` so appointments and ids stay stable. */
const cuerpo = (lista) =>
  lista.map((o) => ({
    ...(o._id ? { _id: o._id } : {}),
    name: o.nombre.trim(),
    durationMinutes: Number(o.duracion),
    price: Number(o.precio),
  }));

export const payloadVariantes = (variantes) => cuerpo(variantes);
export const payloadAdicionales = (adicionales) => cuerpo(adicionales);

/** How many variants and add-ons a catalog service has (for the pills of the services table). */
export const resumenDeServicio = (servicio) => ({
  variantes: servicio?.variants?.length || 0,
  adicionales: servicio?.addOns?.length || 0,
});
